import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getToken, isAuthed } from "@/lib/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sparkles,
  CreditCard,
  Mic,
  CalendarClock,
  Loader2,
  CheckCircle2,
  XCircle,
  Download,
  Leaf,
  ShoppingBag,
  Copy,
} from "lucide-react";

const PLATFORMS = [
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
  { id: "facebook", label: "Facebook" },
  { id: "x", label: "X (Twitter)" },
  { id: "youtube", label: "YouTube" },
  { id: "linkedin", label: "LinkedIn" },
];

const FORMATS = [
  { id: "hook", label: "Hook" },
  { id: "caption", label: "Caption" },
  { id: "script", label: "Script" },
];

const DEFAULT_VOICE_SCRIPT =
  "Hey, I'm Kenny. I started Coastal Roots Botanica because I believe everyone deserves to grow something real with their own hands.";

function StatusBadge({ ok, label }) {
  return (
    <Badge
      variant="outline"
      data-testid={`badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={
        ok
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-accent/40 bg-accent/10 text-accent"
      }
    >
      {ok ? (
        <CheckCircle2 className="mr-1 h-3 w-3" />
      ) : (
        <XCircle className="mr-1 h-3 w-3" />
      )}
      {ok ? "Live" : "Pending key"}
    </Badge>
  );
}

function SectionHeader({ icon: Icon, eyebrow, title, subtitle, badge }) {
  return (
    <CardHeader className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          <Icon className="h-4 w-4" />
          {eyebrow}
        </div>
        {badge}
      </div>
      <div className="space-y-1">
        <CardTitle className="font-serif text-3xl text-foreground">
          {title}
        </CardTitle>
        {subtitle && (
          <CardDescription className="text-base">{subtitle}</CardDescription>
        )}
      </div>
    </CardHeader>
  );
}

function PendingState({ message }) {
  return (
    <div
      data-testid="pending-state"
      className="rounded-lg border border-dashed border-accent/40 bg-accent/5 p-6 text-sm text-foreground/80"
    >
      <p className="font-medium text-accent">Integration not yet configured</p>
      <p className="mt-1 text-muted-foreground">{message}</p>
    </div>
  );
}

// =====================================================================
// STRIPE CARD
// =====================================================================
function StripeCard({ health }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [lastStatus, setLastStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const stripeFlag = searchParams.get("stripe");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const fetchStatus = async () => {
      if (!sessionId) return;
      setStatusLoading(true);
      try {
        const res = await api.get(`/checkout/status`, { params: { session_id: sessionId } });
        setLastStatus(res.data);
        if (res.data.payment_status === "paid") {
          toast.success("Stripe payment confirmed", {
            description: `${res.data.mode === "subscription" ? "Membership" : "Product"} • ${res.data.customer_email || "guest"}`,
          });
        } else if (stripeFlag === "cancel") {
          toast("Stripe checkout cancelled", { description: "No charge was made." });
        }
      } catch (e) {
        toast.error("Could not fetch session status", {
          description: e?.response?.data?.detail || e.message,
        });
      } finally {
        setStatusLoading(false);
      }
    };
    fetchStatus();
    if (stripeFlag) {
      // Clean query params after first run
      const next = new URLSearchParams(searchParams);
      next.delete("stripe");
      next.delete("session_id");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startMembership = async () => {
    setMembershipLoading(true);
    try {
      const res = await api.post(`/checkout/membership`);
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Membership checkout failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setMembershipLoading(false);
    }
  };

  const startProduct = async () => {
    setProductLoading(true);
    try {
      const res = await api.post(`/checkout/product`, {
        product_slug: "mini-succulent-trio",
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Product checkout failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setProductLoading(false);
    }
  };

  return (
    <Card data-testid="card-stripe" className="border-border/60">
      <SectionHeader
        icon={CreditCard}
        eyebrow="01 · Stripe"
        title="Membership & one-time checkout"
        subtitle="Hosted Stripe Checkout. Real test mode. The $50/month Skool subscription and a $34 Mini Succulent Trio one-off."
        badge={<StatusBadge ok={!!health?.stripe?.configured} label="Stripe" />}
      />
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border/60 bg-card p-5">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Community Membership
            </p>
            <p className="mt-1 font-serif text-2xl">$50 <span className="text-base text-muted-foreground">/ month</span></p>
            <p className="mt-1 text-sm text-muted-foreground">
              Recurring subscription · Skool access
            </p>
            <Button
              data-testid="btn-stripe-membership"
              onClick={startMembership}
              disabled={membershipLoading || !health?.stripe?.configured}
              className="mt-4 w-full"
            >
              {membershipLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Test $50/mo checkout
            </Button>
          </div>
          <div className="rounded-lg border border-border/60 bg-card p-5">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Mini Succulent Trio
            </p>
            <p className="mt-1 font-serif text-2xl">$34 <span className="text-base text-muted-foreground">one-time</span></p>
            <p className="mt-1 text-sm text-muted-foreground">
              One-off product test purchase
            </p>
            <Button
              data-testid="btn-stripe-product"
              onClick={startProduct}
              disabled={productLoading || !health?.stripe?.configured}
              variant="secondary"
              className="mt-4 w-full"
            >
              {productLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              <ShoppingBag className="h-4 w-4" />
              Test product checkout
            </Button>
          </div>
        </div>

        {(lastStatus || statusLoading) && (
          <div
            data-testid="stripe-status-box"
            className="rounded-lg border border-primary/30 bg-primary/5 p-5"
          >
            {statusLoading ? (
              <div className="flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Confirming session…
              </div>
            ) : (
              <div className="grid gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span data-testid="stripe-payment-status" className="font-medium">
                    {lastStatus.payment_status}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Mode</span>
                  <span className="font-medium">{lastStatus.mode}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Customer</span>
                  <span className="font-medium">
                    {lastStatus.customer_email || "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Session</span>
                  <span className="truncate font-mono text-xs">
                    {lastStatus.session_id}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Test card: <span className="font-mono">4242 4242 4242 4242</span> · any future date · any CVC · any ZIP.
        </p>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// AI GENERATOR CARD
// =====================================================================
function AIGeneratorCard({ health }) {
  const [platform, setPlatform] = useState("instagram");
  const [format, setFormat] = useState("caption");
  const [topic, setTopic] = useState("Why I prune my bonsai on rainy mornings");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!topic.trim()) {
      toast.error("Give it a topic to write about.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post(`/ai/generate-post`, {
        platform,
        format,
        topic: topic.trim(),
      });
      setResult(res.data);
    } catch (e) {
      toast.error("AI generation failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <Card data-testid="card-ai" className="border-border/60">
      <SectionHeader
        icon={Sparkles}
        eyebrow="02 · AI Content"
        title="On-brand social copywriter"
        subtitle="Claude Sonnet 4.5 via emergentintegrations — speaks in Kenny's voice. Coastal, earthy, calm. No guru-bro."
        badge={<StatusBadge ok={!!health?.ai?.configured} label="AI" />}
      />
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="ai-platform">Platform</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger id="ai-platform" data-testid="select-ai-platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`option-platform-${p.id}`}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ai-format">Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger id="ai-format" data-testid="select-ai-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMATS.map((f) => (
                  <SelectItem key={f.id} value={f.id} data-testid={`option-format-${f.id}`}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ai-topic">Topic</Label>
          <Input
            id="ai-topic"
            data-testid="input-ai-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. The slow art of repotting a juniper bonsai"
          />
        </div>
        <Button
          data-testid="btn-ai-generate"
          onClick={generate}
          disabled={loading || !health?.ai?.configured}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Generate
        </Button>

        {result && (
          <div
            data-testid="ai-result"
            className="space-y-4 rounded-lg border border-border/60 bg-card p-5"
          >
            {result.hook && (
              <div>
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Hook</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copy(result.hook)}
                    data-testid="btn-copy-hook"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <p className="font-serif text-xl leading-snug">{result.hook}</p>
              </div>
            )}
            <Separator />
            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Body</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copy(result.content)}
                  data-testid="btn-copy-body"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              <p className="whitespace-pre-line text-sm leading-relaxed">{result.content}</p>
            </div>
            {result.hashtags?.length > 0 && (
              <>
                <Separator />
                <div className="flex flex-wrap gap-2">
                  {result.hashtags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="bg-secondary text-secondary-foreground"
                    >
                      {tag.startsWith("#") ? tag : `#${tag}`}
                    </Badge>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// VOICE CARD
// =====================================================================
function VoiceCard({ health }) {
  const [voiceCfg, setVoiceCfg] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [scriptText, setScriptText] = useState(DEFAULT_VOICE_SCRIPT);
  const [clips, setClips] = useState([]);
  const [generating, setGenerating] = useState(false);
  const audioRef = useRef(null);
  const [currentAudio, setCurrentAudio] = useState(null);

  const configured = !!health?.elevenlabs?.configured;

  useEffect(() => {
    if (!configured) return;
    (async () => {
      setVoiceLoading(true);
      try {
        const cfg = await api.get(`/voice/config`);
        setVoiceCfg(cfg.data);
        if (cfg.data?.sample_text) setScriptText(cfg.data.sample_text);
        const list = await api.get(`/voice/clips`);
        setClips(list.data.clips || []);
      } catch (e) {
        // Surface clearly; this only runs when configured == true
        toast.error("Could not fetch voice config", {
          description: e?.response?.data?.detail || e.message,
        });
      } finally {
        setVoiceLoading(false);
      }
    })();
  }, [configured]);

  const generate = async () => {
    if (!scriptText.trim()) {
      toast.error("Give Kenny something to say.");
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post(`/voice/generate`, { text: scriptText.trim() });
      const src = `data:audio/mpeg;base64,${res.data.audio_base64}`;
      setCurrentAudio({ src, id: res.data.id });
      // Refresh clip list
      const list = await api.get(`/voice/clips`);
      setClips(list.data.clips || []);
    } catch (e) {
      toast.error("Voice generation failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card data-testid="card-voice" className="border-border/60">
      <SectionHeader
        icon={Mic}
        eyebrow="03 · ElevenLabs"
        title="Kenny's voiceover studio"
        subtitle="Generates an MP3 of any script in Kenny's chosen voice — warm, grounded, conversational."
        badge={<StatusBadge ok={configured} label="ElevenLabs" />}
      />
      <CardContent className="space-y-6">
        {!configured ? (
          <PendingState message="Paste ELEVENLABS_API_KEY into backend/.env and restart. The endpoint will flip live the moment the key lands." />
        ) : (
          <>
            <div
              data-testid="voice-config"
              className="rounded-lg border border-border/60 bg-card p-5"
            >
              {voiceLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading voice…
                </div>
              ) : voiceCfg ? (
                <div className="grid gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">
                      Voice
                    </p>
                    <p className="font-serif text-xl">{voiceCfg.voice_name}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">
                      voice_id
                    </p>
                    <p className="font-mono text-xs">{voiceCfg.voice_id}</p>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="voice-script">Script</Label>
              <Textarea
                id="voice-script"
                data-testid="textarea-voice-script"
                rows={5}
                value={scriptText}
                onChange={(e) => setScriptText(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="btn-voice-generate"
                onClick={generate}
                disabled={generating}
              >
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
                Generate
              </Button>
              {currentAudio && (
                <a
                  href={currentAudio.src}
                  download={`coastal-roots-${currentAudio.id}.mp3`}
                  data-testid="btn-voice-download"
                >
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4" />
                    Download MP3
                  </Button>
                </a>
              )}
            </div>
            {currentAudio && (
              <audio
                ref={audioRef}
                data-testid="audio-voice-player"
                controls
                src={currentAudio.src}
                className="w-full"
              />
            )}

            {clips.length > 0 && (
              <div className="space-y-3">
                <Separator />
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Recent clips
                </p>
                <div className="space-y-2">
                  {clips.slice(0, 5).map((c) => (
                    <div
                      key={c.id}
                      data-testid={`voice-clip-${c.id}`}
                      className="flex items-center justify-between rounded-md border border-border/60 bg-card p-3 text-sm"
                    >
                      <div className="min-w-0 flex-1 pr-3">
                        <p className="truncate">{c.text}</p>
                        <p className="text-xs text-muted-foreground">
                          {c.duration_estimate_sec}s · {c.voice_name}
                        </p>
                      </div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/voice/clips/${c.id}/audio?token=${encodeURIComponent(getToken() || "")}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`download-voice-clip-${c.id}`}
                      >
                        <Button variant="ghost" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// SOCIAL SCHEDULER CARD
// =====================================================================
function defaultScheduleDateLocal() {
  // 60 minutes from now, local TZ, formatted for datetime-local input
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function SocialCard({ health }) {
  const [content, setContent] = useState(
    "A quiet morning in the studio. Today we're potting a baby jade — slow, deliberate, hands in the soil."
  );
  const [selected, setSelected] = useState(["linkedin"]);
  const [scheduleLocal, setScheduleLocal] = useState(defaultScheduleDateLocal());
  const [mediaUrls, setMediaUrls] = useState("");
  const [posts, setPosts] = useState([]);
  const [scheduling, setScheduling] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const configured = !!health?.ayrshare?.configured;

  const refreshList = async () => {
    if (!configured) return;
    setLoadingList(true);
    try {
      const list = await api.get(`/social/scheduled`);
      setPosts(list.data.posts || []);
    } catch (e) {
      // Quiet — the list is non-critical
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured]);

  const togglePlatform = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const minScheduleIso = useMemo(() => {
    const d = new Date(Date.now() + 15 * 60 * 1000); // 15 min floor
    return d;
  }, []);

  const schedule = async () => {
    if (!content.trim()) {
      toast.error("Write something to post.");
      return;
    }
    if (selected.length === 0) {
      toast.error("Pick at least one platform.");
      return;
    }
    const localDate = new Date(scheduleLocal);
    if (Number.isNaN(localDate.getTime())) {
      toast.error("Pick a valid schedule date.");
      return;
    }
    if (localDate < minScheduleIso) {
      toast.error("Schedule date must be at least 15 minutes in the future.");
      return;
    }
    const isoUtc = localDate.toISOString();
    const media = mediaUrls
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    setScheduling(true);
    try {
      const res = await api.post(`/social/schedule`, {
        content: content.trim(),
        platforms: selected,
        schedule_date_iso: isoUtc,
        media_urls: media,
      });
      toast.success("Post scheduled", {
        description: res.data?.ayrshare_response?.id || "Ayrshare accepted",
      });
      await refreshList();
    } catch (e) {
      toast.error("Schedule failed", {
        description:
          (typeof e?.response?.data?.detail === "string"
            ? e.response.data.detail
            : JSON.stringify(e?.response?.data?.detail)) || e.message,
      });
    } finally {
      setScheduling(false);
    }
  };

  return (
    <Card data-testid="card-social" className="border-border/60">
      <SectionHeader
        icon={CalendarClock}
        eyebrow="04 · Ayrshare"
        title="Multi-platform scheduler"
        subtitle="One unified API for Instagram, TikTok, Facebook, X, YouTube, and LinkedIn."
        badge={<StatusBadge ok={configured} label="Ayrshare" />}
      />
      <CardContent className="space-y-6">
        {!configured ? (
          <PendingState message="Paste AYRSHARE_API_KEY into backend/.env and restart. The endpoint will flip live the moment the key lands." />
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="social-content">Content</Label>
              <Textarea
                id="social-content"
                data-testid="textarea-social-content"
                rows={4}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Platforms</Label>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {PLATFORMS.map((p) => (
                  <label
                    key={p.id}
                    htmlFor={`platform-${p.id}`}
                    className="flex cursor-pointer items-center gap-2 rounded-md border border-border/60 bg-card p-3 text-sm"
                  >
                    <Checkbox
                      id={`platform-${p.id}`}
                      data-testid={`checkbox-platform-${p.id}`}
                      checked={selected.includes(p.id)}
                      onCheckedChange={() => togglePlatform(p.id)}
                    />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="social-date">Schedule (local time)</Label>
                <Input
                  id="social-date"
                  data-testid="input-social-date"
                  type="datetime-local"
                  value={scheduleLocal}
                  onChange={(e) => setScheduleLocal(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Must be ≥15 min from now. Sent to Ayrshare as ISO 8601 UTC.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="social-media">Media URLs (one per line, optional)</Label>
                <Textarea
                  id="social-media"
                  data-testid="textarea-social-media"
                  rows={3}
                  value={mediaUrls}
                  onChange={(e) => setMediaUrls(e.target.value)}
                  placeholder="https://example.com/bonsai.jpg"
                />
              </div>
            </div>
            <Button
              data-testid="btn-social-schedule"
              onClick={schedule}
              disabled={scheduling}
            >
              {scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
              Schedule post
            </Button>

            <Separator />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Scheduled posts
                </p>
                <Button variant="ghost" size="sm" onClick={refreshList} data-testid="btn-refresh-posts">
                  {loadingList ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
                </Button>
              </div>
              {posts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No scheduled posts yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {posts.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`social-post-${p.id}`}
                      className="rounded-md border border-border/60 bg-card p-3 text-sm"
                    >
                      <p className="line-clamp-2">{p.content}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span>{new Date(p.schedule_date_iso).toLocaleString()}</span>
                        <span>·</span>
                        <span>{(p.platforms || []).join(", ")}</span>
                        <Badge variant="outline" className="ml-auto">
                          {p.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// PAGE
// =====================================================================
export default function POC() {
  const [health, setHealth] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthed()) {
      const next = `${location.pathname}${location.search}` || "/poc";
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }
    setAuthChecked(true);
  }, [navigate, location.pathname, location.search]);

  useEffect(() => {
    if (!authChecked) return;
    (async () => {
      try {
        const res = await api.get(`/poc/health`);
        setHealth(res.data);
      } catch (e) {
        toast.error("Could not load POC health", {
          description: e?.response?.data?.detail || e.message,
        });
      }
    })();
  }, [authChecked]);

  if (!authChecked) {
    return (
      <div
        data-testid="poc-auth-loading"
        className="flex min-h-screen items-center justify-center bg-background text-muted-foreground"
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Checking access…
      </div>
    );
  }

  return (
    <div data-testid="poc-page" className="min-h-screen bg-background">
      <div className="mx-auto max-w-4xl px-6 py-12 sm:py-20">
        <header className="mb-12 flex items-start gap-4 sm:mb-16">
          <div className="rounded-full border border-primary/40 bg-primary/10 p-3 text-primary">
            <Leaf className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
              Phase 0 · Integration POC
            </p>
            <h1 className="font-serif text-4xl text-foreground sm:text-5xl lg:text-6xl">
              Coastal Roots Botanica
            </h1>
            <p className="max-w-2xl text-base text-muted-foreground sm:text-lg">
              Four integrations, one proof. Stripe checkout, on-brand AI copy,
              Kenny&apos;s voiceover, and a unified social scheduler — wired to real
              providers, no mocks.
            </p>
          </div>
        </header>

        <div className="space-y-8">
          <StripeCard health={health} />
          <AIGeneratorCard health={health} />
          <VoiceCard health={health} />
          <SocialCard health={health} />
        </div>

        <footer className="mt-16 border-t border-border/60 pt-6 text-xs text-muted-foreground">
          Built for Kenny. Slow, deliberate, hands in the soil.
        </footer>
      </div>
    </div>
  );
}
